import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fcaData } from "@/mocks/fca";
import { estadoFitoConfig, riscoConfig } from "@/mocks/cta";
import TreeProfileHeader from "./components/TreeProfileHeader";
import TreeQuickStats from "./components/TreeQuickStats";
import TreeTabs from "./components/TreeTabs";
import TreeLocationCard from "./components/TreeLocationCard";
import TreeBotanicalCard from "./components/TreeBotanicalCard";
import TreeHealthRiskPanel from "./components/TreeHealthRiskPanel";
import TreeInspectionTable from "./components/TreeInspectionTable";
import TreePhotoGallery from "./components/TreePhotoGallery";
import TreeInterferenceList from "./components/TreeInterferenceList";
import TreeManagementPanel from "./components/TreeManagementPanel";
import TreeDocumentsTable from "./components/TreeDocumentsTable";
import TreeTimeline from "./components/TreeTimeline";

export default function FCAPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("visao-geral");

  // Use mock data directly - in future, fetch by id
  const data = fcaData;

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const ef = estadoFitoConfig[data.estadoFitossanitario] || { badge: "", label: data.estadoFitossanitario };
  const rk = riscoConfig[data.classeRisco] || { badge: "", label: data.classeRisco };

  const renderTab = () => {
    switch (activeTab) {
      case "visao-geral":
        return <TabVisaoGeral data={data} ef={ef} rk={rk} formatDate={formatDate} />;
      case "localizacao":
        return <TabContentWrapper><TreeLocationCard data={data} /></TabContentWrapper>;
      case "botanicos":
        return <TabContentWrapper><TreeBotanicalCard data={data} /></TabContentWrapper>;
      case "fitossanidade":
        return <TabContentWrapper><TreeHealthRiskPanel data={data} /></TabContentWrapper>;
      case "vistorias":
        return <TabContentWrapper><TreeInspectionTable vistorias={data.vistorias} /></TabContentWrapper>;
      case "fotos":
        return <TabContentWrapper><TreePhotoGallery fotos={data.fotos} /></TabContentWrapper>;
      case "interferencias":
        return <TabContentWrapper><TreeInterferenceList interferencias={data.interferencias} /></TabContentWrapper>;
      case "manejo":
        return <TabContentWrapper><TreeManagementPanel manejo={data.manejo} /></TabContentWrapper>;
      case "documentos":
        return <TabContentWrapper><TreeDocumentsTable documentos={data.documentos} /></TabContentWrapper>;
      case "historico":
        return <TabContentWrapper><TreeTimeline timeline={data.timeline} /></TabContentWrapper>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <TreeProfileHeader data={data} />

      {/* Quick Stats */}
      <TreeQuickStats data={data} />

      {/* Tabs */}
      <TreeTabs activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {renderTab()}
      </div>
    </div>
  );
}

function TabContentWrapper({ children }: { children: React.ReactNode }) {
  return <div className="mt-0">{children}</div>;
}

function TabVisaoGeral({
  data,
  ef,
  rk,
  formatDate,
}: {
  data: typeof fcaData;
  ef: { badge: string; label: string };
  rk: { badge: string; label: string };
  formatDate: (d: string) => string;
}) {
  return (
    <div className="space-y-5">
      {/* Executive summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Summary card */}
          <div className="bg-white rounded-card border border-background-200/70 p-5">
            <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
              <i className="ri-eye-line text-accent-500"></i>
              Resumo Executivo
            </h3>
            <p className="text-sm text-foreground-700 leading-relaxed">
              Indivíduo arbóreo da espécie <strong>{data.nomePopular}</strong> (<em>{data.nomeCientifico}</em>), cadastrado no CTA de {data.municipio}/{data.uf} em {formatDate(data.dataCadastro)}. 
              Encontra-se em <span className={`badge text-xs mx-0.5 align-middle ${ef.badge}`}>{ef.label}</span> estado fitossanitário e classificado como risco <span className={`badge text-xs mx-0.5 align-middle ${rk.badge}`}>{rk.label}</span>. 
              Possui DAP de {data.dap} cm, altura de {data.altura} m e diâmetro de copa de {data.diametroCopa} m. 
              A última vistoria foi realizada em {formatDate(data.ultimaVistoria)} pelo técnico {data.tecnicoResponsavel}, que recomendou: <strong>{data.manejoRecomendado}</strong>.
              Conta com {data.fotosCount} fotos, {data.vistorias.length} vistorias e {data.documentos.length} documentos vinculados à sua ficha cadastral.
            </p>
          </div>

          {/* Key data cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniCard icon="ri-plant-line" label="Tipo" value={data.tipo} color="primary" />
            <MiniCard icon="ri-calendar-line" label="Cadastro" value={formatDate(data.dataCadastro)} color="accent" />
            <MiniCard icon="ri-user-line" label="Responsável" value={data.tecnicoResponsavel} color="secondary" />
            <MiniCard icon="ri-folder-chart-line" label="Projeto" value={data.projetoVinculado.split("—")[0].trim()} color="accent" />
          </div>

          {/* Last recommendation */}
          <div className="bg-white rounded-card border border-background-200/70 p-5">
            <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
              <i className="ri-tools-line text-primary-500"></i>
              Última Recomendação Técnica
            </h3>
            <div className="flex items-start gap-3 p-3 bg-primary-50/30 rounded-lg border border-primary-200/50">
              <span className="w-9 h-9 rounded-md bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-lightbulb-line text-primary-600"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">{data.manejoRecomendado}</p>
                <p className="text-xs text-foreground-600 mt-0.5">{data.fitossanidade.parecerTecnico.substring(0, 200)}...</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar: Alerts + mini timeline */}
        <div className="space-y-4">
          {/* Alerts */}
          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
              <i className="ri-notification-3-line text-warning-500"></i>
              Alertas
            </h3>
            {data.alertas.length === 0 ? (
              <p className="text-xs text-foreground-500">Nenhum alerta ativo</p>
            ) : (
              <div className="space-y-2">
                {data.alertas.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-accent-50/30 rounded-lg border border-accent-200/50">
                    <i className={`text-sm mt-0.5 ${
                      a.nivel === "info" ? "ri-information-line text-accent-600" :
                      a.nivel === "warning" ? "ri-alert-line text-warning-600" :
                      "ri-error-warning-line text-danger-600"
                    }`}></i>
                    <p className="text-xs text-foreground-700 leading-relaxed">{a.mensagem}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mini timeline */}
          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
              <i className="ri-history-line text-foreground-600"></i>
              Últimos Eventos
            </h3>
            <div className="space-y-3">
              {data.timeline.slice(0, 4).map((evt) => (
                <div key={evt.id} className="flex gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 mt-1.5 shrink-0"></span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-900 font-medium leading-tight">{evt.titulo}</p>
                    <p className="text-[10px] text-foreground-500">{formatDate(evt.data)}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                const el = document.querySelector('[data-tab="historico"]');
                if (el instanceof HTMLElement) el.click();
              }}
              className="text-xs text-accent-500 hover:text-accent-600 font-medium mt-3 flex items-center gap-1"
            >
              Ver histórico completo
              <i className="ri-arrow-right-line text-[10px]"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniCard({ icon, label, value, color }: { icon: string; label: string; value: string; color: string }) {
  const colors: Record<string, { bg: string; icon: string; bar: string }> = {
    primary: { bg: "bg-primary-50", icon: "text-primary-600", bar: "bg-primary-500" },
    accent: { bg: "bg-accent-50", icon: "text-accent-600", bar: "bg-accent-500" },
    secondary: { bg: "bg-secondary-50", icon: "text-secondary-600", bar: "bg-secondary-500" },
    warning: { bg: "bg-warning-50", icon: "text-warning-600", bar: "bg-warning-500" },
  };
  const c = colors[color] || colors.primary;

  return (
    <div className="bg-white rounded-card border border-background-200/70 p-3 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-[3px] ${c.bar}`}></div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`w-6 h-6 rounded-md ${c.bg} flex items-center justify-center`}>
          <i className={`${icon} ${c.icon} text-xs`}></i>
        </span>
      </div>
      <p className="text-xs text-foreground-900 font-medium leading-tight truncate">{value}</p>
      <p className="text-[10px] text-foreground-500">{label}</p>
    </div>
  );
}